import { Col, Container, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { ToggleButton, ToggleButtonGroup } from "@amzn/stencil-react-components/toggle-button";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { MessageBannersBox } from "@amzn/it-shop-shared-components";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useHardwareOrdersQuery } from "@/common/hooks/use-hardware-orders-query";
import { useNotificationStore } from "@/common/stores/use-notification-store";
import { useSession } from "@/common/providers/session-provider";
import React, { FC, useState } from "react";
import { HardwareOrdersTable } from "./components/HardwareOrdersTable/hardware-orders-table";
import { LegacyHardwareOrdersTable } from "./components/HardwareOrdersTable/legacy-hardware-orders-table";
import { HardwareSupportFlyout } from "@/common/components/HardwareSupportFlyout/hardware-support-flyout";
import { useLocation } from "react-router-dom";
import { IT_SHOP_URL, PERMISSIONS } from "@/config";
import "./orders-page.style.scss";

const SOFTWARE_ORDERS_URL = `${IT_SHOP_URL}/orders?tab=0`;

export const OrdersPage: FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [showFlyout, setShowFlyout] = useState(params.get("flyoutHardwareSupport") === "true");
  const { orders, isLoading, hasError, degraded, refetch } = useHardwareOrdersQuery();
  const { hasPermission } = useSession();
  const showLcrExperience = hasPermission(PERMISSIONS.USE_LCR);
  const banners = useNotificationStore((state) => state.banners);
  const removeBanner = useNotificationStore((state) => state.removeBanner);
  const addBanner = useNotificationStore((state) => state.addBanner);

  const warningBannerId = React.useRef<string | null>(null);
  const clearWarningBanner = React.useCallback(() => {
    if (warningBannerId.current) {
      removeBanner(warningBannerId.current);
      warningBannerId.current = null;
    }
  }, [removeBanner]);

  // Tracked by ref alone, not reconciled against the banner store: once the user
  // (or another view) dismisses this warning while still degraded, we leave it
  // dismissed for the session instead of re-adding it on every render.
  React.useEffect(() => {
    if (degraded && !warningBannerId.current) {
      warningBannerId.current = addBanner({
        type: "warning",
        message: "Some of your orders couldn't be loaded. Showing the ones we have.",
      });
    } else if (!degraded) {
      clearWarningBanner();
    }
  }, [degraded, addBanner, clearWarningBanner]);

  React.useEffect(() => () => clearWarningBanner(), [clearWarningBanner]);

  const bannerTypeMap: Record<string, MessageBannerType> = {
    error: MessageBannerType.Error,
    success: MessageBannerType.Success,
    warning: MessageBannerType.Warning,
    informational: MessageBannerType.Informational,
  };

  const mapBannersToMessages = () =>
    banners.map((banner) => ({
      id: banner.id,
      message: banner.message,
      type: bannerTypeMap[banner.type] ?? MessageBannerType.Informational,
    }));

  const handleToggleChange = (_event: React.FormEvent<HTMLElement>, value: string | string[] | undefined) => {
    if (value === "software") {
      window.location.href = SOFTWARE_ORDERS_URL;
    }
  };

  return (
    <>
      <div className="orders-page-banners">
        <Container isFullWidth={false}>
          <View padding="S300">
            <MessageBannersBox
              messages={mapBannersToMessages()}
              onRemove={removeBanner}
              strings={{ persistMessage: "If the error persists, ", ctiLinkLabel: "contact support" }}
              ctiUrl=""
            />
          </View>
        </Container>
      </div>

      <Container isFullWidth={false}>
        <Col width="100%" padding="S300">
          <Text fontSize="T400" color="neutral90">
            {getString("myOrders.topBar")}
          </Text>

          <Spacer height={20} />

          <Row gridGap="S400" width="100%" justifyContent="space-between" alignItems="center">
            <ToggleButtonGroup onChange={handleToggleChange} data-testid="orders-toggle">
              <ToggleButton isSelected={false} value="software">
                {getString("myOrders.softwareToggle")}
              </ToggleButton>
              <ToggleButton isSelected={true} value="hardware">
                {getString("myOrders.hardwareToggle")}
              </ToggleButton>
            </ToggleButtonGroup>
            <Spacer />
            <Link href="#" data-testid="get-order-support" onClick={() => setShowFlyout(true)}>
              Get order support
            </Link>
          </Row>

          <Spacer height={20} />

          {showLcrExperience ? (
            <HardwareOrdersTable orders={orders} isLoading={isLoading} hasError={hasError} onRetry={refetch} />
          ) : (
            <LegacyHardwareOrdersTable orders={orders} isLoading={isLoading} hasError={hasError} onRetry={refetch} />
          )}
        </Col>

        {showFlyout && <HardwareSupportFlyout onClose={() => setShowFlyout(false)} />}
      </Container>
    </>
  );
};

export default OrdersPage;
